import { WorkOrderSourceType } from "features/WorkOrders/constants/work-order.constants";
import { QuotationItemType } from "features/Quotations/constants/quotation.constants";
import * as Yup from "yup";

const itemTypes = Object.values(QuotationItemType) as QuotationItemType[];

const ItemSchema = Yup.object({
  id: Yup.number().optional(),
  type: Yup.mixed<QuotationItemType>()
    .oneOf(itemTypes)
    .required("Type is required"),
  description: Yup.string().trim().required("Description is required"),
  productId: Yup.mixed<number | string>().nullable().optional(),
  quantity: Yup.number()
    .typeError("Quantity must be a number")
    .positive("Quantity must be greater than zero")
    .required("Quantity is required"),
  unit: Yup.string().trim().required("Unit is required"),
  rate: Yup.number()
    .typeError("Rate must be a number")
    .min(0, "Rate cannot be negative")
    .required("Rate is required"),
});

const SubcontractRowSchema = Yup.object({
  id: Yup.number().optional(),
  supplierId: Yup.number().required("Subcontractor is required"),
  description: Yup.string().trim().required("Description is required"),
  agreedAmount: Yup.number()
    .typeError("Must be a number")
    .min(0, "Cannot be negative")
    .required("Agreed amount is required"),
  labourCount: Yup.number().nullable().optional(),
  notes: Yup.string().nullable().optional(),
});

const ExpenseSchema = Yup.object({
  id: Yup.number().optional(),
  expenseCategoryId: Yup.number().required("Category is required"),
  amount: Yup.number()
    .typeError("Amount must be a number")
    .min(0, "Amount cannot be negative")
    .required("Amount is required"),
  description: Yup.string().nullable().optional(),
});

const schema = Yup.object({
  title: Yup.string().required("The title field is required"),

  sourceType: Yup.mixed<WorkOrderSourceType>()
    .oneOf(Object.values(WorkOrderSourceType))
    .default(WorkOrderSourceType.QUOTATION),

  sourceId: Yup.number().required("The source field is required"),

  description: Yup.string().nullable(),

  items: Yup.array()
    .of(ItemSchema)
    .min(1, "Please add at least one line item")
    .required("Please add at least one line item"),

  expenses: Yup.array().of(ExpenseSchema).optional(),

  subcontracts: Yup.array().of(SubcontractRowSchema).optional(),

  agreedAmount: Yup.number()
    .typeError("Must be a number")
    .min(0, "Cannot be negative")
    .nullable()
    .when(["isEdit", "status"], ([isEdit, status], s) => {
      const startedStatuses = new Set([
        "IN_PROGRESS",
        "ON_HOLD",
        "COMPLETED",
        "CANCELLED",
      ]);
      if (isEdit && startedStatuses.has(String(status ?? ""))) {
        return s.optional();
      }
      return s.required("Agreed amount is required");
    }),
  code: Yup.string()
    .trim()
    .transform((v) => (v ? v.toUpperCase() : v))
    .min(4, "Code must be at least 4 characters")
    .max(50, "Code must be at most 50 characters")
    .matches(
      /^[A-Za-z0-9][A-Za-z0-9\-_/&]*[A-Za-z0-9]$/,
      "Code must start and end with a letter or number, and may only contain letters, numbers, hyphens, underscores, slashes, or &",
    )
    .nullable()
    .optional(),

  plannedStartDate: Yup.string()
    .nullable()
    .optional()
    .test("valid-date", "Enter a valid date", (v) => {
      if (!v) return true;
      return !Number.isNaN(new Date(v).getTime());
    }),

  plannedEndDate: Yup.string()
    .nullable()
    .optional()
    .test("valid-date", "Enter a valid date", (v) => {
      if (!v) return true;
      return !Number.isNaN(new Date(v).getTime());
    })
    .test("end-gte-start", "End date cannot be earlier than start date.", function (v) {
      const start = this.parent.plannedStartDate;
      if (!v || !start) return true;
      return v >= start;
    }),

  isEdit: Yup.boolean().required(),

  status: Yup.string().optional(),
});

export default schema;
